/**
 * Translation between PocketBase records (snake_case, loosely typed) and the
 * domain model (camelCase, strict). Every mapper is total: a missing or
 * malformed column yields a sane default rather than throwing, because a single
 * bad row must never stall the sweeper for every other lead.
 */
import type {
  DailyReportRecord,
  LeadDetail,
  DeliveryRecord,
  DeliveryStatus,
  Destination,
  LeadRecord,
  QualityLevel,
  ReportScope,
  RoutingStatus,
} from "../types.ts";
import { DELIVERY_STATUSES, QUALITY_LEVELS, ROUTING_STATUSES } from "../types.ts";
import { toIsoOrNow } from "../core/normalize.ts";

export type PbRecord = Record<string, unknown>;

function str(value: unknown): string {
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value : String(value);
}

function bool(value: unknown): boolean {
  return value === true || value === "true" || value === 1;
}

function int(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
}

/** PocketBase json columns arrive parsed, but tolerate a JSON string too. */
function stringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(str).filter((entry) => entry.length > 0);
  if (typeof value === "string" && value.trim().startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(str).filter((entry) => entry.length > 0);
    } catch {
      // fall through
    }
  }
  return [];
}

function stringMap(value: unknown): Record<string, string> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const result: Record<string, string> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      result[key] = str(entry);
    }
    return result;
  }
  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      return stringMap(JSON.parse(value));
    } catch {
      // fall through
    }
  }
  return {};
}

/** Qualifying answers round-trip through a json column as [{label,value}]. */
function detailList(value: unknown): LeadDetail[] {
  const raw = typeof value === "string" && value.trim().startsWith("[") ? safeParse(value) : value;
  if (!Array.isArray(raw)) return [];

  const details: LeadDetail[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const label = str((entry as Record<string, unknown>).label);
    const detail = str((entry as Record<string, unknown>).value);
    if (label && detail) details.push({ label, value: detail });
  }
  return details;
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function objectOf(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

/** Empty date columns must stay empty, not become "now". */
function optionalDate(value: unknown): string {
  const raw = str(value);
  return raw ? toIsoOrNow(raw) : "";
}

/**
 * An unset partnership bound must be `null`, never epoch-zero or "now" —
 * routing reads `null` as "unbounded on that end", and any real date there
 * would silently start excluding leads.
 */
function boundaryDate(value: unknown): Date | null {
  const raw = str(value);
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  const raw = str(value);
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

// ---------------------------------------------------------------------------

export function toLeadRecord(record: PbRecord): LeadRecord {
  return {
    id: str(record.id),
    sourceKey: str(record.source_key),
    sourceCollection: str(record.source_collection),
    sourceRecordId: str(record.source_record_id),
    siteKey: str(record.site_key),
    submittedAt: optionalDate(record.submitted_at) || toIsoOrNow(str(record.created)),

    name: str(record.name),
    email: str(record.email),
    phone: str(record.phone),
    company: str(record.company),
    city: str(record.city),
    state: str(record.state),
    postalCode: str(record.postal_code),
    message: str(record.message),
    interest: str(record.interest),
    details: detailList(record.details),
    sourceUrl: str(record.source_url),
    referrer: str(record.referrer),
    utm: stringMap(record.utm),
    ip: str(record.ip),
    userAgent: str(record.user_agent),
    raw: objectOf(record.raw),

    quality: oneOf<QualityLevel>(record.quality, QUALITY_LEVELS, "clean"),
    qualityReasons: stringArray(record.quality_reasons),
    isDuplicate: bool(record.is_duplicate),
    duplicateOf: str(record.duplicate_of),
    routingStatus: oneOf<RoutingStatus>(record.routing_status, ROUTING_STATUSES, "pending"),
    routedAt: optionalDate(record.routed_at),
    created: toIsoOrNow(str(record.created)),
  };
}

export function toDestination(record: PbRecord): Destination {
  return {
    id: str(record.id),
    key: str(record.key),
    name: str(record.name),
    active: bool(record.active),
    channel: "email",
    emails: stringArray(record.emails),
    ccEmails: stringArray(record.cc_emails),
    siteKeys: stringArray(record.site_keys),
    deliverFrom: boundaryDate(record.deliver_from),
    deliverUntil: boundaryDate(record.deliver_until),
    minQuality: oneOf<"clean" | "suspect">(record.min_quality, ["clean", "suspect"], "clean"),
    dailyReportEnabled: bool(record.daily_report_enabled),
    dailyReportEmails: stringArray(record.daily_report_emails),
    includeCsvInDaily: bool(record.include_csv_in_daily),
    notes: str(record.notes),
  };
}

export function toDeliveryRecord(record: PbRecord): DeliveryRecord {
  return {
    id: str(record.id),
    lead: str(record.lead),
    destination: str(record.destination),
    dedupeKey: str(record.dedupe_key),
    channel: "email",
    status: oneOf<DeliveryStatus>(record.status, DELIVERY_STATUSES, "pending"),
    attempts: int(record.attempts),
    nextAttemptAt: optionalDate(record.next_attempt_at),
    lastError: str(record.last_error),
    providerMessageId: str(record.provider_message_id),
    idempotencyKey: str(record.idempotency_key),
    recipients: stringArray(record.recipients),
    sentAt: optionalDate(record.sent_at),
    deliveredAt: optionalDate(record.delivered_at),
    created: toIsoOrNow(str(record.created)),
  };
}

export function toDailyReportRecord(record: PbRecord): DailyReportRecord {
  return {
    id: str(record.id),
    reportDate: str(record.report_date),
    scope: oneOf<ReportScope>(record.scope, ["partner", "internal"], "internal"),
    destination: str(record.destination),
    dedupeKey: str(record.dedupe_key),
    status: oneOf<"pending" | "sent" | "failed">(record.status, ["pending", "sent", "failed"], "pending"),
    attempts: int(record.attempts),
    stats: objectOf(record.stats),
    html: str(record.html),
    lastError: str(record.last_error),
    sentAt: optionalDate(record.sent_at),
  };
}

// --- domain -> PocketBase --------------------------------------------------

/** PocketBase rejects `undefined`; omit instead. Also truncates runaway strings. */
export function cleanPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined) continue;
    result[key] = value;
  }
  return result;
}

/** Keep a single oversized field from failing an entire insert. */
export function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 3)}...`;
}
